import React, { useState, useEffect } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { useIntl } from 'react-intl';
import { ChangeProjectDropDown } from '../../../../../GuestHomepage/view/frontend/src/ChangeProjectDropDown';
import {
  Text,
  Input,
  PinIcon,
  PhoneIcon,
  SearchIcon,
  getCreateOrderPayload,
  createOrder,
  fetchDistributors,
  DistributorResponse,
  LoadingIcon,
  DialogOverlay,
  DialogHeader,
  Dialog,
  DialogContent,
  DialogFooter,
  getShouldTriggerProjectConfirmationDialog,
} from '../../../../../UILibrary';
import { definedTranslation } from '../i18n';

export const FindDistributors = () => {
  const intl = useIntl();

  const { projects } = useProjectsProvider();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isLoadingDistributors, setIsLoadingDistributors] = useState(false);
  const [distributors, setDistributors] = useState<DistributorResponse[]>([]);
  const [matchingDistributors, setMatchingDistributors] = useState<
    DistributorResponse[]
  >([]);

  const openFindDistributorsDialog = () => {
    const sku = window.productAttributeJson?.sku?.value;
    setIsLoadingDistributors(true);
    fetchDistributors(sku)
      .then((response) => {
        setDistributors(
          response.map((item) => ({
            ...item,
            parsed_address: item.street_address,
          })),
        );
      })
      .finally(() => {
        setIsLoadingDistributors(false);
      });

    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleOpen = () => {
    const currentProjectId = currentProject?.externalId;

    // Open the create project modal if no project is selected
    if (!currentProjectId || !currentProject) {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            eventLocation: 'Digital sampling',
            successCallback: () => {
              openFindDistributorsDialog();
            },
          },
        }),
      );

      return;
    }

    // Complete any missing project data
    if (!isProjectDataComplete(currentProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: currentProjectId,
        successCallback: async () => {
          openFindDistributorsDialog();
        },
      });

      return;
    }

    const shouldTriggerProjectConfirmationDialog =
      getShouldTriggerProjectConfirmationDialog();

    if (shouldTriggerProjectConfirmationDialog) {
      window.dispatchEvent(
        new CustomEvent('open-project-selection-dialog', {
          detail: {
            currentProjectId,
            analyticsEventLocation: 'Find distributors',
            onCurrentProjectSave: () => {
              openFindDistributorsDialog();
            },
          },
        }),
      );
    } else {
      openFindDistributorsDialog();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';

    const productSku = window.productAttributeJson?.sku?.value;

    // Open the create project modal if no project is selected
    if (!currentProject) {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            eventLocation: 'Digital sampling',
            successCallback: () => {
              openFindDistributorsDialog();
            },
          },
        }),
      );

      return;
    }

    if (productSku) {
      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: currentProject.externalId,
          successCallback: async (project: ProjectModel) => {
            const orderData = getCreateOrderPayload(
              productSku,
              project,
              'Get distributor',
            );

            createOrder(orderData!);
          },
        });

        return;
      }

      const orderData = getCreateOrderPayload(
        productSku,
        currentProject,
        'Get distributor',
      );

      createOrder(orderData!);
    }
  };

  useEffect(() => {
    window.addEventListener('open-find-distributors-dialog', handleOpen);

    return () => {
      window.removeEventListener('open-find-distributors-dialog', handleOpen);
    };
  }, []);

  useEffect(() => {
    const results = distributors.filter(
      (distributor) =>
        distributor.phone.toLowerCase().includes(searchText.toLowerCase()) ||
        distributor.street_address
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        distributor.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    setMatchingDistributors(results);
  }, [searchText, distributors]);

  return (
    <DialogOverlay isOpen={isOpen} handleClose={handleClose}>
      <Dialog isOpen={isOpen}>
        <DialogHeader
          title={intl?.formatMessage(
            definedTranslation.findDistributors.distributorsLabel,
          )}
          handleClose={handleClose}
        />
        <DialogContent>
          {isLoadingDistributors ? (
            <div className="flex w-full h-full justify-center items-center">
              <LoadingIcon />
            </div>
          ) : (
            <>
              <ChangeProjectDropDown
                showSelectedProject={true}
                projects={projects}
                currentProject={currentProject}
                onProjectChange={(projectId) => {
                  setCurrentProjectId(projectId);
                }}
              />

              <Input
                leftIcon={<SearchIcon />}
                placeholder={intl?.formatMessage(
                  definedTranslation.findDistributors.searchPlaceholder,
                )}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              <div className="flex-1 flex flex-col gap-1 overflow-auto">
                {matchingDistributors.map((distributor) => (
                  <div
                    className="py-3 sm:py-5 flex flex-col gap-1 border-b border-b-[#F2F2F2]"
                    key={distributor.distributor_id}
                  >
                    <Text variant="H3" sm="H2">
                      {distributor.name}
                    </Text>
                    {distributor?.street_address && (
                      <div className="flex items-center gap-2">
                        <PinIcon />
                        <Text>
                          {distributor.street_address} {distributor.state}{' '}
                          {distributor.postal_code}
                        </Text>
                      </div>
                    )}
                    {distributor?.phone && (
                      <div className="flex items-center gap-2">
                        <PhoneIcon />
                        <Text>{distributor.phone}</Text>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogFooter handleClose={handleClose} />
      </Dialog>
    </DialogOverlay>
  );
};
